import api from './api';

export interface MetaConfig {
  access_token_set: boolean;
  ad_account_id: string | null;
  page_id: string | null;
  ig_user_id: string | null;
}

export interface CapiConfig {
  pixel_id: string | null;
  /** O token nunca é devolvido pela API — só se está presente. */
  has_access_token: boolean;
  enabled: boolean;
  test_event_code: string | null;
  graph_version: string;
}

export interface CapiTestResult {
  success: boolean;
  ok?: boolean;
  status?: number;
  error?: string;
  eventsReceived?: number;
  skipped?: 'disabled' | 'unconfigured';
}

export interface CapiEventStat {
  event_name: string;
  action_source: string;
  total: number;
  ok: number;
  taxa_sucesso: number;
  ultimo: string;
}

export interface CapiStats {
  dias: number;
  por_evento: CapiEventStat[];
  erros_frequentes: Array<{
    event_name: string;
    error_message: string;
    n: number;
    ultimo: string;
  }>;
}

export interface MetaAdSummary {
  impressions: number;
  reach: number;
  clicks: number;
  spend: number;
  cpm: number;
  cpc: number;
  ctr: number;
  conversions: number;
  date_start: string;
  date_stop: string;
}

export interface MetaCampaign {
  id: string;
  name: string;
  status: string;
  objective: string;
  created_time: string;
  insights: {
    impressions: number;
    reach: number;
    clicks: number;
    spend: number;
    cpm: number;
    cpc: number;
    ctr: number;
  } | null;
}

export interface MetaIGPost {
  id: string;
  caption: string | null;
  media_type: string;
  media_url: string | null;
  permalink: string;
  timestamp: string;
  like_count: number;
  comments_count: number;
}

export interface MetaIGData {
  posts: MetaIGPost[];
  account: {
    impressions: number;
    reach: number;
    profile_views: number;
    followers_count: number;
    media_count: number;
    username: string | null;
  } | null;
}

export interface MetaTokenValidation {
  valid: boolean;
  user?: { id: string; name: string };
  error?: string;
}

export type MetaCampaignObjective =
  | 'OUTCOME_AWARENESS'
  | 'OUTCOME_TRAFFIC'
  | 'OUTCOME_ENGAGEMENT'
  | 'OUTCOME_LEADS'
  | 'OUTCOME_SALES';

export interface CreateCampaignPayload {
  name: string;
  objective: MetaCampaignObjective;
  daily_budget: number;
  status?: 'ACTIVE' | 'PAUSED';
}

export type MetaOptimizationGoal =
  | 'REACH'
  | 'LINK_CLICKS'
  | 'POST_ENGAGEMENT'
  | 'LEAD_GENERATION'
  | 'OFFSITE_CONVERSIONS'
  | 'VIDEO_VIEWS';

export type MetaPublisherPlatform = 'facebook' | 'instagram' | 'audience_network' | 'messenger';
export type MetaInstagramPosition = 'stream' | 'story' | 'explore' | 'reels' | 'explore_home';
export type MetaFacebookPosition = 'feed' | 'right_hand_column' | 'instant_article' | 'marketplace' | 'story' | 'search' | 'reels';

export interface MetaTargeting {
  geo_locations: { countries: string[] };
  age_min?: number;
  age_max?: number;
  genders?: number[];
  publisher_platforms?: MetaPublisherPlatform[];
  instagram_positions?: MetaInstagramPosition[];
  facebook_positions?: MetaFacebookPosition[];
}

export interface CreateAdSetPayload {
  campaign_id: string;
  name: string;
  optimization_goal: MetaOptimizationGoal;
  daily_budget: number;
  targeting: MetaTargeting;
  status?: 'ACTIVE' | 'PAUSED';
}

export interface MetaAdSet {
  id: string;
  name: string;
  status: string;
  optimization_goal: string;
  daily_budget: string;
  targeting: MetaTargeting;
  insights: { impressions: number; reach: number; clicks: number; spend: number } | null;
}

export interface CreateAdPayload {
  ad_set_id: string;
  name: string;
  message: string;
  link: string;
  image_url?: string;
  headline?: string;
  call_to_action?: string;
  status?: 'ACTIVE' | 'PAUSED';
}

export interface MetaAd {
  id: string;
  name: string;
  status: string;
  creative: {
    id: string;
    name: string;
    thumbnail_url?: string;
  } | null;
  insights: { impressions: number; reach: number; clicks: number; spend: number } | null;
}

class MarketingMetaService {
  async getConfig(): Promise<MetaConfig> {
    const res = await api.get('/admin/marketing/meta/config');
    return res.data.data;
  }

  async saveConfig(config: {
    access_token?: string;
    ad_account_id?: string;
    page_id?: string;
    ig_user_id?: string;
  }): Promise<void> {
    await api.put('/admin/marketing/meta/config', config);
  }

  async validateToken(): Promise<MetaTokenValidation> {
    const res = await api.get('/admin/marketing/meta/validate-token');
    return { valid: res.data.valid, user: res.data.user, error: res.data.error };
  }

  async getSummary(preset = 'last_30d'): Promise<MetaAdSummary | null> {
    const res = await api.get(`/admin/marketing/meta/summary?preset=${preset}`);
    return res.data.data;
  }

  async getCampaigns(preset = 'last_30d', limit = 15): Promise<{
    data: MetaCampaign[];
    not_configured?: boolean;
    access_status?: 'ok' | 'no_data' | 'no_access' | 'error';
    missing_permissions?: string[];
    message?: string;
  }> {
    const res = await api.get(`/admin/marketing/meta/campaigns?preset=${preset}&limit=${limit}`);
    return {
      data: res.data.data || [],
      not_configured: res.data.not_configured,
      access_status: res.data.access_status,
      missing_permissions: res.data.missing_permissions,
      message: res.data.message,
    };
  }

  async getInstagram(limit = 9): Promise<MetaIGData & {
    not_configured?: boolean;
    access_status?: 'ok' | 'no_data' | 'no_access' | 'error';
    missing_permissions?: string[];
  }> {
    const res = await api.get(`/admin/marketing/meta/instagram?limit=${limit}`);
    return {
      ...res.data.data,
      not_configured: res.data.not_configured,
      access_status: res.data.access_status,
      missing_permissions: res.data.missing_permissions,
    };
  }

  async checkPermissions(): Promise<{ status: string; granted?: string[]; missing_permissions?: string[]; reason?: string } | null> {
    const res = await api.get('/admin/marketing/meta/permissions');
    return res.data.data;
  }

  async createCampaign(payload: CreateCampaignPayload): Promise<{ id: string }> {
    const res = await api.post('/admin/marketing/meta/campaigns', payload);
    return res.data.data;
  }

  async updateCampaignStatus(id: string, status: 'ACTIVE' | 'PAUSED'): Promise<void> {
    await api.patch(`/admin/marketing/meta/campaigns/${id}/status`, { status });
  }

  async deleteCampaign(id: string): Promise<void> {
    await api.delete(`/admin/marketing/meta/campaigns/${id}`);
  }

  // Ad Sets
  async getAdSets(campaignId: string, preset = 'last_30d'): Promise<MetaAdSet[]> {
    const res = await api.get(`/admin/marketing/meta/campaigns/${campaignId}/ad-sets?preset=${preset}`);
    return res.data.data || [];
  }

  async createAdSet(payload: CreateAdSetPayload): Promise<{ id: string }> {
    const res = await api.post('/admin/marketing/meta/ad-sets', payload);
    return res.data.data;
  }

  async updateAdSetStatus(id: string, status: 'ACTIVE' | 'PAUSED'): Promise<void> {
    await api.patch(`/admin/marketing/meta/ad-sets/${id}/status`, { status });
  }

  async deleteAdSet(id: string): Promise<void> {
    await api.delete(`/admin/marketing/meta/ad-sets/${id}`);
  }

  // Ads
  async getAds(adSetId: string, preset = 'last_30d'): Promise<MetaAd[]> {
    const res = await api.get(`/admin/marketing/meta/ad-sets/${adSetId}/ads?preset=${preset}`);
    return res.data.data || [];
  }

  async createAd(payload: CreateAdPayload): Promise<{ id: string; creative_id: string }> {
    const res = await api.post('/admin/marketing/meta/ads', payload);
    return res.data.data;
  }

  async updateAdStatus(id: string, status: 'ACTIVE' | 'PAUSED'): Promise<void> {
    await api.patch(`/admin/marketing/meta/ads/${id}/status`, { status });
  }

  async deleteAd(id: string): Promise<void> {
    await api.delete(`/admin/marketing/meta/ads/${id}`);
  }

  // ─── Conversions API ───────────────────────────────────────────────────────

  async getCapiConfig(): Promise<CapiConfig> {
    const res = await api.get('/admin/marketing/capi/config');
    return res.data.data;
  }

  async saveCapiConfig(config: {
    pixel_id?: string;
    capi_access_token?: string;
    capi_enabled?: string;
    capi_test_event_code?: string;
  }): Promise<void> {
    await api.put('/admin/marketing/capi/config', config);
  }

  /** Cria as tabelas de log e as colunas de atribuição, se faltarem. */
  async setupCapi(): Promise<void> {
    await api.post('/admin/marketing/capi/setup');
  }

  /** Dispara um evento de teste. Aparece em Events Manager → Test Events. */
  async testCapi(email?: string): Promise<CapiTestResult> {
    const res = await api.post('/admin/marketing/capi/test', email ? { email } : {});
    return { success: res.data.success, ...res.data.data };
  }

  async getCapiStats(days = 7): Promise<CapiStats> {
    const res = await api.get(`/admin/marketing/capi/stats?days=${days}`);
    return res.data.data;
  }
}

export default new MarketingMetaService();
